/**
 * =============================================================================
 * IDS CRM Web - Distributor Depots Card
 * =============================================================================
 *
 * PURPOSE:
 * Lists a distributor's depots (head office + secondaries) with add/edit,
 * Find Coordinates (shared geocoding endpoint, postcode fallback included)
 * and the head-office radio — exactly one holder, moved by checking another
 * depot; the API enforces the invariants.
 *
 * =============================================================================
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from './ConfirmDialog';
import CountrySelect from './CountrySelect';
import UseMyLocationButton from './UseMyLocationButton';
import { deviceGeocodeFields } from '../utils/deviceLocationPin';
import { validateCoordinatePair } from '../utils/coordinates';

const EMPTY_FORM = {
  depot_name: '',
  address_line_1: '',
  address_line_2: '',
  town_city: '',
  county_region: '',
  state_province: '',
  postcode: '',
  country_code: 'GB',
  lat: '',
  lng: '',
  geocode_source: '',
  geocode_status: '',
  geocode_accuracy: ''
};

const addressOneLiner = (depot) => [
  depot.address_line_1,
  depot.town_city,
  depot.state_province,
  depot.postcode,
  depot.country_code
].filter(Boolean).join(', ') || '—';

const coordinateChip = (depot) => {
  if (!depot.lat || !depot.lng) return { label: 'No coordinates', className: 'chip-missing' };
  if (depot.geocode_status === 'manual' || depot.geocode_source === 'manual') return { label: 'Manual pin', className: 'chip-manual' };
  return { label: 'Geocoded', className: 'chip-geocoded' };
};

function DepotsCard({ distributorId, contacts = [], onAddContact }) {
  const { authFetch, user } = useAuth();
  const { confirmElement, requestConfirm } = useConfirm();
  // RR2/RR3 + Gareth 2026-08-04: depot management is operational — team
  // leaders, managers, admins and operations coordinators edit; commercial
  // view the chain read-only (server enforces the same on the depot write
  // routes via ROLES.DEPOT_EDIT).
  const canEdit = ['team_leader', 'manager', 'admin', 'operations_coordinator'].includes(user?.role);

  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });

  const [showModal, setShowModal] = useState(false);
  const [editingDepot, setEditingDepot] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeMessage, setGeocodeMessage] = useState({ status: '', text: '' });

  // Expandable "sites serviced" (2026-08-03): clicking the count lists the
  // customers/sites behind it.
  const [expandedDepotId, setExpandedDepotId] = useState(null);
  const [depotSites, setDepotSites] = useState(null);

  const toggleDepotSites = async (depot) => {
    if (expandedDepotId === depot.depot_id) {
      setExpandedDepotId(null);
      setDepotSites(null);
      return;
    }
    setExpandedDepotId(depot.depot_id);
    setDepotSites(null);
    try {
      const response = await authFetch(`/api/v1/depots/${depot.depot_id}/sites`);
      const data = await response.json();
      setDepotSites(response.ok ? (data.data || []) : []);
    } catch {
      setDepotSites([]);
    }
  };

  const fetchDepots = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch(`/api/v1/distributors/${distributorId}/depots`);
      if (!response.ok) throw new Error('Failed to load depots');
      const data = await response.json();
      setDepots(data.data || []);
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [authFetch, distributorId]);

  useEffect(() => {
    fetchDepots();
  }, [fetchDepots]);

  const openAddModal = () => {
    setEditingDepot(null);
    setFormData(EMPTY_FORM);
    setGeocodeMessage({ status: '', text: '' });
    setShowModal(true);
  };

  const openEditModal = (depot) => {
    setEditingDepot(depot);
    setFormData({
      depot_name: depot.depot_name || '',
      address_line_1: depot.address_line_1 || '',
      address_line_2: depot.address_line_2 || '',
      town_city: depot.town_city || '',
      county_region: depot.county_region || '',
      state_province: depot.state_province || '',
      postcode: depot.postcode || '',
      country_code: depot.country_code || 'GB',
      lat: depot.lat != null ? String(depot.lat) : '',
      lng: depot.lng != null ? String(depot.lng) : '',
      geocode_source: depot.geocode_source || '',
      geocode_status: depot.geocode_status || '',
      geocode_accuracy: depot.geocode_accuracy || ''
    });
    setGeocodeMessage({ status: '', text: '' });
    setShowModal(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Typing coordinates by hand supersedes any provider metadata
      ...(name === 'lat' || name === 'lng'
        ? { geocode_source: 'manual', geocode_status: 'manual', geocode_accuracy: '' }
        : {})
    }));
  };

  const findCoordinates = async () => {
    const hasAddress = [formData.address_line_1, formData.town_city, formData.postcode]
      .some(value => (value || '').trim());
    if (!hasAddress) {
      setGeocodeMessage({ status: 'error', text: 'Enter a depot address before finding coordinates.' });
      return;
    }

    setGeocoding(true);
    setGeocodeMessage({ status: '', text: '' });
    try {
      const response = await authFetch('/api/v1/geocode-address', {
        method: 'POST',
        body: JSON.stringify({
          addressLine1: formData.address_line_1,
          addressLine2: formData.address_line_2,
          city: formData.town_city,
          countyState: formData.county_region,
          state: formData.state_province,
          postcodeZip: formData.postcode,
          country: formData.country_code
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'No matching address found');
      }
      setFormData(prev => ({
        ...prev,
        lat: String(data.latitude),
        lng: String(data.longitude),
        geocode_source: data.source || 'nominatim',
        geocode_status: 'geocoded',
        geocode_accuracy: data.raw?.type || data.raw?.class || ''
      }));
      setGeocodeMessage({
        status: 'success',
        text: `Matched: ${data.displayName || 'address found'}. Save the depot to confirm.`
      });
    } catch (error) {
      setGeocodeMessage({ status: 'error', text: error.message });
    } finally {
      setGeocoding(false);
    }
  };

  /**
   * Pin the depot where the device is. Geocoding a depot address lands on the
   * street, which for a yard or an industrial unit is often the wrong gate.
   */
  const handleDevicePin = (result) => {
    if (result.ok) {
      setFormData(prev => ({
        ...prev,
        lat: result.lat,
        lng: result.lng,
        ...deviceGeocodeFields(result)
      }));
    }
    setGeocodeMessage({ status: result.status, text: result.message });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // The lat/lng boxes here have always been typeable but never checked, so a
    // slip put the depot in the sea and dragged the map with it.
    const coordinateError = validateCoordinatePair(formData.lat, formData.lng, { noun: 'depots' });
    if (coordinateError) {
      setGeocodeMessage({ status: 'error', text: coordinateError });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = {
        depot_name: formData.depot_name,
        address_line_1: formData.address_line_1,
        address_line_2: formData.address_line_2,
        town_city: formData.town_city,
        county_region: formData.county_region,
        state_province: formData.state_province,
        postcode: formData.postcode,
        country_code: formData.country_code,
        lat: formData.lat,
        lng: formData.lng,
        geocode_source: formData.geocode_source || undefined,
        geocode_status: formData.geocode_status || undefined,
        geocode_accuracy: formData.geocode_accuracy || undefined
      };

      const response = await authFetch(
        editingDepot
          ? `/api/v1/depots/${editingDepot.depot_id}`
          : `/api/v1/distributors/${distributorId}/depots`,
        {
          method: editingDepot ? 'PUT' : 'POST',
          body: JSON.stringify(payload)
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save depot');

      setShowModal(false);
      setMessage({ text: editingDepot ? 'Depot updated' : 'Depot added', type: 'success' });
      fetchDepots();
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const makeHeadOffice = async (depot) => {
    try {
      const response = await authFetch(`/api/v1/depots/${depot.depot_id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_head_office: true })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to move head office');
      setMessage({ text: `"${depot.depot_name}" is now the head office`, type: 'success' });
      fetchDepots();
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    }
  };

  const removeDepot = (depot) => {
    requestConfirm({
      title: 'Remove depot?',
      message: depot.is_head_office
        ? `"${depot.depot_name}" is the head office — removing it moves the flag to the oldest remaining depot. Sites serviced by it lose the link.`
        : `Remove "${depot.depot_name}"? Sites serviced by it lose the link.`,
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        try {
          const response = await authFetch(`/api/v1/depots/${depot.depot_id}`, { method: 'DELETE' });
          const data = await response.json();
          if (!response.ok) throw new Error(data.message || 'Failed to remove depot');
          setMessage({ text: data.message, type: 'success' });
          fetchDepots();
        } catch (error) {
          setMessage({ text: error.message, type: 'error' });
        }
      }
    });
  };

  return (
    <div className="detail-card depots-card">
      {confirmElement}
      <div className="card-header">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1m4 0h1M9 13h1m4 0h1M9 17h1m4 0h1"/>
        </svg>
        <h3>DEPOTS</h3>
        {canEdit && (
          <button type="button" className="btn-secondary depots-add-btn" onClick={openAddModal}>
            + Add Depot
          </button>
        )}
      </div>
      <div className="card-body">
        {message.text && <div className={`depot-message ${message.type}`}>{message.text}</div>}

        {loading ? (
          <p className="empty-note">Loading depots...</p>
        ) : depots.length === 0 ? (
          <p className="empty-note">No depots recorded — add the head office to put this distributor on the map.</p>
        ) : (
          <table className="depots-table">
            <thead>
              <tr>
                <th>Depot</th>
                <th>Address</th>
                <th>Coordinates</th>
                <th>Sites serviced</th>
                <th>Head office</th>
                {canEdit && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {depots.map(depot => {
                const chip = coordinateChip(depot);
                const siteCount = depot.serviced_site_count || 0;
                const isExpanded = expandedDepotId === depot.depot_id;
                const depotContacts = contacts.filter(c => c.depot_id === depot.depot_id);
                return (
                  <React.Fragment key={depot.depot_id}>
                    <tr>
                      <td className="depot-name">
                        {depot.depot_name}
                        {(depotContacts.length > 0 || onAddContact) && (
                          <div className="depot-contact-list">
                            {depotContacts.map(c => (
                              <span key={c.contact_id} className="depot-contact-row">
                                {c.full_name}{c.phone ? ` · ${c.phone}` : ''}
                              </span>
                            ))}
                            {onAddContact && (
                              <button type="button" className="btn-link depot-add-contact" onClick={() => onAddContact(depot.depot_id)}>
                                Add contact
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td>{addressOneLiner(depot)}</td>
                      <td><span className={`depot-chip ${chip.className}`}>{chip.label}</span></td>
                      <td>
                        {siteCount > 0 ? (
                          <button
                            type="button"
                            className="depot-sites-toggle"
                            onClick={() => toggleDepotSites(depot)}
                          >
                            {siteCount} {isExpanded ? '▾' : '▸'}
                          </button>
                        ) : 0}
                      </td>
                      <td>
                        <label className="depot-ho-radio" title={depot.is_head_office ? 'Head office' : 'Make head office'}>
                          <input
                            type="radio"
                            name={`head-office-${distributorId}`}
                            checked={depot.is_head_office}
                            disabled={!canEdit || depot.is_head_office}
                            onChange={() => makeHeadOffice(depot)}
                          />
                          {depot.is_head_office ? 'Head office' : ''}
                        </label>
                      </td>
                      {canEdit && (
                        <td className="depot-actions">
                          <button type="button" className="btn-icon" onClick={() => openEditModal(depot)}>Edit</button>
                          <button type="button" className="btn-icon delete" onClick={() => removeDepot(depot)}>Remove</button>
                        </td>
                      )}
                    </tr>
                    {isExpanded && (
                      <tr className="depot-sites-row">
                        <td colSpan={canEdit ? 6 : 5}>
                          {depotSites === null ? (
                            <span className="empty-note">Loading sites...</span>
                          ) : depotSites.length === 0 ? (
                            <span className="empty-note">No sites linked to this depot.</span>
                          ) : (
                            <div className="depot-sites-list">
                              {depotSites.map(site => (
                                <Link key={site.site_id} to={`/customers/${site.customer_id}`} className="depot-site-link">
                                  <strong>{site.business_name}</strong>
                                  <span>{site.site_name}</span>
                                  <span className="depot-site-loc">{[site.town_city, site.postcode].filter(Boolean).join(', ')}</span>
                                </Link>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={event => event.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingDepot ? 'Edit Depot' : 'Add Depot'}</h3>
              <button type="button" className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="depot_name">Depot name *</label>
                  <input id="depot_name" name="depot_name" value={formData.depot_name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label htmlFor="depot_address_line_1">Address line 1</label>
                  <input id="depot_address_line_1" name="address_line_1" value={formData.address_line_1} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="depot_address_line_2">Address line 2</label>
                  <input id="depot_address_line_2" name="address_line_2" value={formData.address_line_2} onChange={handleChange} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="depot_town_city">Town / City</label>
                    <input id="depot_town_city" name="town_city" value={formData.town_city} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="depot_county_region">County / Region</label>
                    <input id="depot_county_region" name="county_region" value={formData.county_region} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="depot_state_province">State / Province</label>
                    <input id="depot_state_province" name="state_province" value={formData.state_province} onChange={handleChange} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="depot_postcode">Postcode / Zipcode</label>
                    <input id="depot_postcode" name="postcode" value={formData.postcode} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="depot_country_code">Country</label>
                    <CountrySelect id="depot_country_code" name="country_code" value={formData.country_code} onChange={handleChange} />
                  </div>
                </div>
                <div className="form-row depot-coordinates-row">
                  <div className="form-group">
                    <label htmlFor="depot_lat">Latitude</label>
                    <input id="depot_lat" name="lat" value={formData.lat} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="depot_lng">Longitude</label>
                    <input id="depot_lng" name="lng" value={formData.lng} onChange={handleChange} />
                  </div>
                  <button type="button" className="btn-secondary" onClick={findCoordinates} disabled={geocoding}>
                    {geocoding ? 'Searching...' : 'Find coordinates'}
                  </button>
                  <UseMyLocationButton
                    noun="depot"
                    className="btn-secondary"
                    disabled={geocoding}
                    onResult={handleDevicePin}
                  />
                </div>
                {geocodeMessage.text && (
                  <div className={`geocode-message ${geocodeMessage.status}`}>{geocodeMessage.text}</div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingDepot ? 'Save Changes' : 'Add Depot')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DepotsCard;
